// src/services/auditService/auditAdjustmentService.ts
import {BASE_AUDIT_API, fetchAPI} from '@/services/api';

export interface AuditAdjustmentHeader {
    id?: string;

    companyId: number;
    annualId: number;

    /** Sub-period YOA mark e.g. 2021/22; null = legacy / SINGLE */
    yearOfAssessment?: string | null;

    adjustmentMode: number;

    refNo: string;

    status: number;

    preparedUserId: number;
    preparedDate: string;
    reviewedUserId: number;
    reviewedDate: string;

    lines: AuditAdjustmentLine[];
}

export interface AuditAdjustmentLine {
    // Identifiers (often provided by backend after first save)
    id?: string;
    headerId?: string;
    companyId: number;
    annualId: number;
    fsId: number;

    // Statement type (e.g., 'BS' | 'PL')
    statement: string;

    // Optional extra item naming from backend; not always present in UI-generated payloads
    extraItemName?: string;
    currency?: string;

    // Amounts are frequently computed or optional in UI step prior to persistence
    openingAmount?: number;
    /** Signed adjustment used by draft/UI pairing; `adjustmentAmount` is typically abs. */
    adjustment?: number;
    adjustmentAmount?: number;
    closingAmount?: number;

    adjustHisAmount?: number;

    // Schedule metadata (may be resolved server-side if missing)
    scheduleName?: string;
    scheduleCode?: string;

    /** Dr | Cr after pairing / recompute (UI draft; not always persisted) */
    entry?: string | null;

    // include auditWorkingPaperInterface all interface
    details: any[];
}

export interface AdjustByPaper {
    refNo: string;
    adjustment: number;
}

export interface FsSimpleData {
    fsId: number;
    level: number;
    name: string;
    /** Base EN name from category (before alias merge) */
    nameEn?: string | null;
    /** Base ZH name from category (before alias merge) */
    nameZh?: string | null;
    /** Company alias EN for current / later years */
    aliasEn?: string | null;
    /** Company alias ZH for current / later years */
    aliasZh?: string | null;
    /** 1 = alias locked (not editable) */
    aliasLocked?: number | null;
    /** BS / PL statement marker from AuditFsItemVO */
    statement?: string | null;
    code: string;
    entry: 'Dr' | 'Cr';  // 明確定義只有 'Dr' 或 'Cr' 兩個值
    scheduleName: string;
    scheduleCode: string;
    openingAmount: number;
    adjustment: AdjustByPaper[];
    finalAmount: number;
    lastYearAudited?: number;
    adjustByPaper: boolean;  // 新增：表示是否通过 working paper 调整
    wpStatus?: number | null;
    wpIssueRemark?: string | null;
    outstanding?: boolean | null;
    outstandingRemark?: string | null;
    issueStatus?: number | null;
    notes?: string | null;
    /** Company custom Level-3 under PL-COT / PL-OI / PL-AE */
    isCustom?: boolean;
    customNodeId?: number | null;
    templateCodeRef?: string | null;
    lastYearChangeAudited?: number | null;
    amountsByYearOfAssessment?: Record<string, number> | null;
    children?: FsSimpleData[];  // 改为可选，因为叶子节点可能没有 children
}

export interface AdjustRecord {
    companyId: number;
    annualId: number;
    inCorpDate: string;
    bsLine: FsSimpleData[];
    plLine: FsSimpleData[];
}

export interface AuditFsTreeSelection {
    fsId: number;
    name: string;
    alias: string;
    scheduleCode: string;
    scheduleName: string;
    entry?: string | null; // 添加 entry 字段（Dr 或 Cr）

    children: AuditFsTreeSelection[];
}

const base = `${BASE_AUDIT_API}/adjustment`;

/**
 * 清理 header 数据，移除不应该发送的 id
 * 只有当 id 是从后端获取且需要更新时才保留
 */
function cleanHeaderForSubmit(body: AuditAdjustmentHeader): AuditAdjustmentHeader {
    const cleaned: AuditAdjustmentHeader = { ...body };
    
    // 移除根级别不应该存在的字段（这些字段应该只在 lines[].details[] 中）
    // 这些字段可能在前端数据处理过程中被意外添加到根级别，需要在发送给后端前清理
    const fieldsToRemoveFromRoot = [
        'salesIncome',
        'relatedRows',
        'transactionTest',
        'cutOffThisYearTest',
        'cutOffNextYearTest',
        'purchases',
        'bankInterestIncome',
        'otherInterestIncome',
        'commissionPayments',
        'taxDeductible',
        'nonTaxDeductible',
        'bankRow',
        'debtRow',
        'feesRow',
        'principalSubsidiaryRow',
        'costRow',
        'impairmentLossRow',
        'dueFromToDirectors',
        'dueFromToDirRow',
        'gainExchange',
        'lossExchange',
        'gainDisposal',
        'lossDisposal',
        'locationOfPropertyRow',
        'commercialAllowanceRow',
        'industrialAllowanceRow',
        'furnitureFixtureRow',
        'computerHardSoftRow',
        'fullPayMotorRow',
        'hirePurchaseMotorRow',
        'machineryRow',
        'liftEscalatorRow',
        'equipmentRow',
        'ppeRate',
        'intangibleRows',
        'taxiCabsCostRows',
        'taxiCabsAccumulationRows',
        'bankLoans',
        'vehicleLoans',
        'rows',
        'monthsForPeriod',
        'extractPL',
        'deletedMonthsByCol',
        'rentalTotalsByCol',
        'staffRow',
        'workerRow',
        'directorRow',
        'directorQuarterRow',
        'staffTotalAdj',
        'workerTotalAdj',
        'directorTotalAdj',
        'mpfTotalAdj',
        'quarterTotalAdj',
        'subsidiaryRow',
        'associateRow',
        'intangibleRow',
        'taxiLicenseDisRow',
        'fullPayTaxiCabDisRow',
        'hirePurchaseTaxiCabDisRow',
        'trustsRow',
        'annualInfo'
    ];
    
    fieldsToRemoveFromRoot.forEach(field => {
        if ((cleaned as any)[field] !== undefined) {
            delete (cleaned as any)[field];
        }
    });
    
    // 如果 id 是 undefined、null 或者看起来像默认值（如 1），删除该字段
    // 注意：只有大于 0 的有效 id 且确认是后端返回的才保留
    if (cleaned.id === undefined || cleaned.id === null) {
        delete cleaned.id;
    }
    
    // 清理 lines 中的 id
    if (cleaned.lines && Array.isArray(cleaned.lines)) {
        cleaned.lines = cleaned.lines.map(line => {
            const cleanedLine = { ...line };
            
            // 如果 line id 是 undefined 或 null，删除该字段
            if (cleanedLine.id === undefined || cleanedLine.id === null) {
                delete cleanedLine.id;
            }
            if (cleanedLine.headerId === undefined || cleanedLine.headerId === null) {
                delete cleanedLine.headerId;
            }
            
            // 清理 details 中的 id
            if (cleanedLine.details && Array.isArray(cleanedLine.details)) {
                cleanedLine.details = cleanedLine.details.map(detail => {
                    const cleanedDetail = { ...detail };
                    
                    // 清理 detail 本身的 id
                    if (cleanedDetail.id === undefined || cleanedDetail.id === null) {
                        delete cleanedDetail.id;
                    }
                    
                    // 清理嵌套的数据结构（如 commonRow、component/payload 等）
                    // 处理 commonRow 数组
                    if (cleanedDetail.commonRow && Array.isArray(cleanedDetail.commonRow)) {
                        cleanedDetail.commonRow = cleanedDetail.commonRow.map((row: any) => {
                            const cleanedRow = { ...row };
                            // 清理 undefined、null 或负数 id（负数id是前端临时生成的）
                            if (cleanedRow.id === undefined || cleanedRow.id === null || 
                                (typeof cleanedRow.id === 'number' && cleanedRow.id < 0)) {
                                delete cleanedRow.id;
                            }
                            return cleanedRow;
                        });
                    }
                    
                    // 处理 component/payload 结构
                    if (cleanedDetail.payload) {
                        cleanedDetail.payload = cleanDetailPayload(cleanedDetail.payload);
                    }
                    
                    return cleanedDetail;
                });
            }
            
            return cleanedLine;
        });
    }
    
    return cleaned;
}

/**
 * 递归清理 payload 中的 id 字段
 * 清理规则：
 * 1. undefined 或 null 的 id 会被删除
 * 2. 负数 id（前端临时生成的新增行标识）会被删除
 * 3. 只保留正数 id（后端返回的真实 id）
 */
function cleanDetailPayload(payload: any): any {
    if (!payload) return payload;
    
    // Helper function to check if id should be removed
    const shouldRemoveId = (id: any): boolean => {
        return id === undefined || id === null || (typeof id === 'number' && id < 0);
    };
    
    // 如果是数组，递归处理每个元素
    if (Array.isArray(payload)) {
        return payload.map(item => {
            if (typeof item === 'object' && item !== null) {
                const cleaned = { ...item };
                if (shouldRemoveId(cleaned.id)) {
                    delete cleaned.id;
                }
                return cleaned;
            }
            return item;
        });
    }
    
    // 如果是对象，遍历其属性
    if (typeof payload === 'object') {
        const cleaned: any = {};
        for (const key in payload) {
            if (payload.hasOwnProperty(key)) {
                const value = payload[key];
                // 如果属性是数组且可能包含有 id 的对象
                if (Array.isArray(value)) {
                    cleaned[key] = value.map((item: any) => {
                        if (typeof item === 'object' && item !== null) {
                            const cleanedItem = { ...item };
                            if (shouldRemoveId(cleanedItem.id)) {
                                delete cleanedItem.id;
                            }
                            return cleanedItem;
                        }
                        return item;
                    });
                } else {
                    cleaned[key] = value;
                }
            }
        }
        return cleaned;
    }
    
    return payload;
}

// POST /adjustment/saveAndPosted base on header
export async function saveAndPosted(body: AuditAdjustmentHeader) {
    // 清理数据，移除不应该发送的 id
    const cleanedBody = cleanHeaderForSubmit(body);
    
    return fetchAPI<Boolean>(`${base}/saveAndPosted`, {
        method: 'POST',
        body: JSON.stringify(cleanedBody),
    });
}

// POST /adjustment/saveOrUpdate
export async function saveOrUpdate(body: AuditAdjustmentHeader) {
    // 清理数据，移除不应该发送的 id
    const cleanedBody = cleanHeaderForSubmit(body);
    
    return fetchAPI<AuditAdjustmentHeader>(`${base}/saveOrUpdate`, {
        method: 'POST',
        body: JSON.stringify(cleanedBody),
    });
}

// POST /adjustment/post/{headerId}?draftUpdate=...  Body: AuditAdjustmentHeader (when draftUpdate=true)
export async function post(headerId: string, draftUpdate: boolean = false, bo?: Partial<AuditAdjustmentHeader> | null) {
    const params = new URLSearchParams({draftUpdate: String(!!draftUpdate)});
    const options: any = {method: 'POST', body: JSON.stringify(bo ?? {})};
    return fetchAPI<boolean>(`${base}/post/${headerId}?${params.toString()}`, options);
}


// POST /adjustment/review/{headerId}?reviewerUserId=...&reviewedDate=...
export async function review(headerId: string, reviewerUserId: number, reviewedDate?: string) {
    const params = new URLSearchParams({reviewerUserId: String(reviewerUserId)});
    if (reviewedDate) params.set('reviewedDate', reviewedDate);
    return fetchAPI<boolean>(`${base}/review/${headerId}?${params.toString()}`, {
        method: 'POST',
    });
}

/**
 * Query adjustment by DR/CR
 * @param companyId Company ID
 * @param annualId Annual ID
 * @param refNo Reference number
 */
export async function queryAdjustByDrCr(companyId: number, annualId: number, refNo: string) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        refNo: refNo
    });
    return fetchAPI<any[]>(`${base}/queryAdjustByDrCr?${params.toString()}`, {
        method: 'GET',
    });
}

/**
 * Query adjustment record by working paper (schedule code)
 * Returns aggregated details from all adjustment headers for a specific schedule code
 * Details are sorted by refNo
 * @param companyId Company ID
 * @param annualId Annual ID
 * @param scheduleCode Schedule code
 */
export async function queryAdjustRecordByWorking(companyId: number, annualId: number, scheduleCode: string) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        scheduleCode: scheduleCode
    });
    return fetchAPI<any[]>(`${base}/queryAdjustRecordByWorking?${params.toString()}`, {
        method: 'GET',
    });
}
